import { useContext, useEffect, useMemo, useState } from 'react';
import api from 'api';
import { LeadStages } from 'models/Lead';
import PopNotificationsContext from 'providers/PopNotifications/PopNotifications.context';
import { DataRoomNode } from 'router/subrouters/DataRoom/models/DataRoomNode';
import { getIsArrayEmpty, getIsNodeFolder, sortDataRoomNodes } from 'utils';
import { StageFilesTree } from '../utils/stageFiles';

type UseStageFilesTreeProps = {
  workflowId: string;
  page: LeadStages;
  dataRoomNodes?: DataRoomNode[];
};

const useStageFilesTree = ({
  workflowId,
  page,
  dataRoomNodes,
}: UseStageFilesTreeProps): StageFilesTree => {
  const [childrenByParent, setChildrenByParent] = useState<
    Record<string, DataRoomNode[]>
  >({});
  const [isLoading, setIsLoading] = useState(false);

  const { popServerError } = useContext(PopNotificationsContext);

  const rootNodes = useMemo(
    () => sortDataRoomNodes(dataRoomNodes || []),
    [dataRoomNodes],
  );

  useEffect(() => {
    let cancelled = false;

    setChildrenByParent({});

    const loadLevel = async (folders: DataRoomNode[]): Promise<void> => {
      if (cancelled || getIsArrayEmpty(folders)) return;

      const levels = await Promise.all(
        folders.map(({ _id }) =>
          api.portfolio.seller
            .getWorkflowConfiguration(workflowId, page, _id)
            .then(({ data }) => ({
              parentId: _id,
              nodes: sortDataRoomNodes(data.dataRoomNodes),
            })),
        ),
      );

      if (cancelled) return;

      setChildrenByParent((prev) =>
        levels.reduce(
          (acc, { parentId, nodes }) => ({ ...acc, [parentId]: nodes }),
          prev,
        ),
      );

      await loadLevel(
        levels.reduce<DataRoomNode[]>(
          (acc, { nodes }) => [...acc, ...nodes.filter(getIsNodeFolder)],
          [],
        ),
      );
    };

    setIsLoading(true);

    loadLevel(rootNodes.filter(getIsNodeFolder))
      .catch(popServerError)
      .finally(() => {
        if (!cancelled) setIsLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [page, popServerError, rootNodes, workflowId]);

  const { nodesById, parentById } = useMemo(() => {
    const nodesById: Record<string, DataRoomNode> = {};
    const parentById: Record<string, string> = {};

    rootNodes.forEach((node) => (nodesById[node._id] = node));

    Object.keys(childrenByParent).forEach((parentId) =>
      childrenByParent[parentId].forEach((node) => {
        nodesById[node._id] = node;
        parentById[node._id] = parentId;
      }),
    );

    return { nodesById, parentById };
  }, [childrenByParent, rootNodes]);

  return useMemo(
    () => ({ rootNodes, childrenByParent, nodesById, parentById, isLoading }),
    [childrenByParent, isLoading, nodesById, parentById, rootNodes],
  );
};

export default useStageFilesTree;
